import React, { useCallback, useEffect, useRef, useState } from "react";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { isExplicitZeroPrice, normalizeComplimentaryPrice } from "../../utils/complimentaryStock";
import PurchasingDocumentDialog from "./PurchasingDocumentDialog";
import { getDeliveryNotePriceTranslator } from "./deliveryNotePriceMessages";
import "./ComplimentaryStock.css";

const positivePrice = (value) => Number.isFinite(Number(value)) && Number(value) > 0;
const estimateSource = (value) => ["tender", "last_purchase", "manual"].includes(value) ? value : null;
const lineKey = (line, index) => JSON.stringify([
  line.id ?? line.temp_id ?? line._destinationKey ?? line._clientKey ?? index, line.item_id,
]);
const decisionKey = (line, index, priceKey, quantityKey) => JSON.stringify([
  line.id ?? line.temp_id ?? line._destinationKey ?? line._clientKey ?? index,
  line.item_id, line[quantityKey], line.unit_level, Number(line[priceKey]), line.price_status, Boolean(line.is_free_stock),
]);

/** Returns prepared lines only after an explicit decision. Cancelling never mutates the draft. */
export function useComplimentaryConfirmation({ isActive = true, scopeKey } = {}) {
  const { languageId } = usePmsTheme();
  const t = getDeliveryNotePriceTranslator(languageId);
  const [request, setRequest] = useState(null);
  const [reasons, setReasons] = useState({});
  const [choices, setChoices] = useState({});
  const [amounts, setAmounts] = useState({});
  const [lastPrices, setLastPrices] = useState({});
  const [estimateSources, setEstimateSources] = useState({});
  const acknowledged = useRef(new Map());
  const resolver = useRef(null);
  const activeRequest = useRef(null);
  const priceRequests = useRef(new Map());
  const scope = useRef({ isActive, scopeKey });
  scope.current = { isActive, scopeKey };
  useEffect(() => {
    resolver.current?.(null);
    resolver.current = null;
    activeRequest.current = null;
    priceRequests.current.clear();
    setRequest(null);
    acknowledged.current.clear();
  }, [isActive, scopeKey]);
  useEffect(() => () => {
    resolver.current?.(null);
    resolver.current = null;
    activeRequest.current = null;
    priceRequests.current.clear();
  }, []);

  const confirmLines = useCallback((lines, {
    priceKey = "unit_price_in_dn", quantityKey = "quantity_in_dn",
    getItemLabel = (line) => line.item_name || line.item?.name || line.description || line.ocr_description || String(line.item_id || "—"),
    getUnitLabel = (line) => line.unit_level || "",
    getLastPrice,
    editAll = false, includeBlank = true,
  } = {}) => {
    if (!scope.current.isActive || resolver.current) return Promise.resolve(null);
    const prepared = lines.map((line) => normalizeComplimentaryPrice(line, priceKey));
    const candidates = prepared.map((line, index) => ({ line, index }))
      .filter(({ line, index }) => editAll || (!line.is_free_stock
        && acknowledged.current.get(lineKey(line, index)) !== decisionKey(line, index, priceKey, quantityKey)
        && (isExplicitZeroPrice(line[priceKey])
          || (includeBlank && (line[priceKey] == null || String(line[priceKey]).trim() === "")))));
    if (!candidates.length) return Promise.resolve(prepared);
    return new Promise((resolve) => {
      resolver.current = resolve;
      setReasons(Object.fromEntries(candidates.map(({ line, index }) => [index, line.free_stock_reason || ""])));
      setChoices(editAll ? Object.fromEntries(candidates.map(({ line, index }) => [index,
        line.is_free_stock ? "complimentary" : line.price_status === "estimated" ? "estimated"
          : positivePrice(line[priceKey]) ? "confirmed" : "missing"])) : {});
      setAmounts(Object.fromEntries(candidates.map(({ line, index }) => [index, positivePrice(line[priceKey]) ? line[priceKey] : ""])));
      setLastPrices({});
      setEstimateSources(Object.fromEntries(candidates.map(({ line, index }) => [index, estimateSource(line.price_estimate_source)])));
      priceRequests.current.clear();
      const nextRequest = { prepared, candidates, priceKey, quantityKey, getItemLabel, getUnitLabel, getLastPrice,
        editAll, scopeKey: scope.current.scopeKey };
      activeRequest.current = nextRequest;
      setRequest(nextRequest);
    });
  }, []);

  const selectChoice = (index, choice) => {
    priceRequests.current.delete(index);
    setChoices((previous) => ({ ...previous, [index]: choice }));
  };
  const loadReferencePrice = async (line, index) => {
    const currentRequest = activeRequest.current;
    if (!currentRequest || typeof currentRequest.getLastPrice !== "function") return;
    const token = {};
    priceRequests.current.set(index, token);
    setChoices((previous) => ({ ...previous, [index]: "last" }));
    setLastPrices((previous) => ({ ...previous, [index]: { status: "loading" } }));
    const isCurrent = () => activeRequest.current === currentRequest && resolver.current
      && scope.current.isActive && currentRequest.scopeKey === scope.current.scopeKey
      && priceRequests.current.get(index) === token;
    try {
      const result = await currentRequest.getLastPrice(line);
      if (!isCurrent()) return;
      setLastPrices((previous) => ({ ...previous, [index]: result && positivePrice(result.price)
        ? { ...result, price: Number(result.price), status: "ready" } : { status: "empty" } }));
    } catch {
      if (isCurrent()) setLastPrices((previous) => ({ ...previous, [index]: { status: "error" } }));
    }
  };
  const validChoice = (index) => ["complimentary", "missing"].includes(choices[index])
    || (["estimated", "confirmed"].includes(choices[index]) && positivePrice(amounts[index]))
    || (choices[index] === "last" && lastPrices[index]?.status === "ready" && positivePrice(lastPrices[index]?.price));
  const settle = (confirmed) => {
    const resolve = resolver.current;
    if (!resolve || !request) return;
    if (confirmed && request.candidates.some(({ index }) => !validChoice(index))) return;
    resolver.current = null;
    activeRequest.current = null;
    priceRequests.current.clear();
    const indices = new Set(request.candidates.map(({ index }) => index));
    const validScope = scope.current.isActive && request.scopeKey === scope.current.scopeKey;
    const result = confirmed && validScope ? request.prepared.map((line, index) => indices.has(index)
      ? { ...line, [request.priceKey]: choices[index] === "last" ? lastPrices[index].price
          : ["estimated", "confirmed"].includes(choices[index]) ? Number(amounts[index]) : 0,
        is_free_stock: choices[index] === "complimentary",
        price_status: choices[index] === "complimentary" ? "confirmed" : choices[index] === "last" ? "estimated" : choices[index],
        price_estimate_source: choices[index] === "last" ? estimateSource(lastPrices[index].source) || "last_purchase"
          : choices[index] === "estimated" ? estimateSources[index] || "manual" : null,
        free_stock_reason: choices[index] === "complimentary" ? String(reasons[index] || "").trim() : "" }
      : line) : null;
    if (result) result.forEach((line, index) => {
      if (indices.has(index)) acknowledged.current.set(lineKey(line, index), decisionKey(line, index, request.priceKey, request.quantityKey));
    });
    setRequest(null);
    resolve(result);
  };

  const dialog = <PurchasingDocumentDialog isOpen={Boolean(request)} title={t("Price options")}
    cancelLabel={t(request?.editAll ? "Cancel" : "Review prices")}
    confirmLabel={t(request?.editAll ? "Apply" : "Confirm and save")}
    confirmDisabled={Boolean(request?.candidates.some(({ index }) => !validChoice(index)))}
    onClose={() => settle(false)} onConfirm={() => settle(true)}
    message={<div className="complimentary-confirm">
      <p>{t(request?.editAll ? "Choose how to record this item's price." : "Choose a price option for each zero or blank price before saving.")}</p>
      <div className="complimentary-confirm__list">
        {request?.candidates.map(({ line, index }) => <div className="complimentary-confirm__item" key={index}>
          <div className="complimentary-confirm__identity"><strong>{request.getItemLabel(line)}</strong>
            <span>{t("Quantity")}: {line[request.quantityKey] ?? "—"} {request.getUnitLabel(line)}</span>
          </div>
          <div className="complimentary-confirm__decision">
            <div role="group" aria-label={`${t("Price status")}: ${request.getItemLabel(line)}`}
              className="complimentary-confirm__choices">
              <label><input type="radio" name={`price-status-${index}`} value="estimated"
                checked={choices[index] === "estimated"}
                onChange={() => selectChoice(index, "estimated")} />
                <span>{t("Estimate")}</span></label>
              {typeof request.getLastPrice === "function" && <label><input type="radio" name={`price-status-${index}`} value="last"
                checked={choices[index] === "last"} onChange={() => loadReferencePrice(line, index)} />
                <span>{t("Use reference price")}</span></label>}
              <label><input type="radio" name={`price-status-${index}`} value="missing"
                checked={choices[index] === "missing"}
                onChange={() => selectChoice(index, "missing")} />
                <span>{t("Unknown")}</span></label>
              <label><input type="radio" name={`price-status-${index}`} value="complimentary"
                checked={choices[index] === "complimentary"}
                onChange={() => selectChoice(index, "complimentary")} />
                <span>{t("Complimentary")}</span></label>
            </div>
          {choices[index] && <small className="complimentary-confirm__hint">{t({
            estimated: "Enter a provisional unit price greater than zero. It will remain unconfirmed.",
            last: "Uses an applicable tender price, or the latest posted purchase price, even if that purchase price was estimated. It remains an estimate until confirmed.",
            missing: "The delivery note does not include a price. It will remain pending, without marking the item as free.",
            complimentary: "The supplier provides this item free of charge. Its price will be zero.",
            confirmed: "Price confirmed from the delivery note.",
          }[choices[index]])}</small>}
          {choices[index] === "last" && <div className="complimentary-confirm__last-price" role="status" aria-live="polite">
            {lastPrices[index]?.status === "loading" && <span>{t("Loading reference price…")}</span>}
            {lastPrices[index]?.status === "ready" && <><strong>
              {lastPrices[index].price} {lastPrices[index].currency || ""}
              {(lastPrices[index].unitLabel || request.getUnitLabel(line)) && <> / {lastPrices[index].unitLabel || request.getUnitLabel(line)}</>}
            </strong><span>{t(lastPrices[index].source === "tender" ? "Estimate · Tender" : "Estimate · Last purchase")}</span>
              {lastPrices[index].source === "last_purchase" && <>
                {lastPrices[index].referencePriceStatus === "estimated" && <small>
                  {t("The previous purchase price was also estimated. Check it against the supplier's document.")}
                </small>}
                {(lastPrices[index].deliveryNoteNumber || lastPrices[index].deliveryDate) && <small>
                  {t("Source delivery note")}: {[lastPrices[index].deliveryNoteNumber, lastPrices[index].deliveryDate].filter(Boolean).join(" · ")}
                </small>}
              </>}
            </>}
            {["empty", "error"].includes(lastPrices[index]?.status) && <>
              <span>{t(lastPrices[index].status === "empty"
                ? "No tender or previous purchase price is available for this item and unit."
                : "The reference price could not be loaded. Please try again.")}</span>
              <button type="button" onClick={() => loadReferencePrice(line, index)}>{t("Try again")}</button>
            </>}
          </div>}
          {["estimated", "confirmed"].includes(choices[index]) && <>
            <label><span>{t(choices[index] === "estimated" ? "Estimated unit price" : "Unit price")}</span>
              <input type="number" min="0" step="0.00000001" value={amounts[index] ?? ""}
                aria-label={`${t(choices[index] === "estimated" ? "Estimated unit price" : "Unit price")}: ${request.getItemLabel(line)}`}
                onChange={(event) => {
                  const value = event.target.value;
                  if (String(value) !== String(amounts[index] ?? "")) {
                    setEstimateSources((previous) => ({ ...previous, [index]: "manual" }));
                  }
                  setAmounts((previous) => ({ ...previous, [index]: value }));
                }} />
            </label>
            {request.editAll && line.price_status === "estimated" && choices[index] === "estimated" &&
              <button type="button" disabled={!positivePrice(amounts[index])}
                onClick={() => selectChoice(index, "confirmed")}>{t("Remove estimate")}</button>}
          </>}
          {choices[index] === "complimentary" && <label><span>{t("Reason (optional)")}</span>
            <input type="text" maxLength={500} value={reasons[index] || ""}
              aria-label={`${t("Reason")}: ${request.getItemLabel(line)}`}
              onChange={(event) => setReasons((previous) => ({ ...previous, [index]: event.target.value }))} />
          </label>}
          </div>
        </div>)}
      </div>
    </div>} />;
  const editLine = async (line, options) => (await confirmLines([line], { ...options, editAll: true }))?.[0] || null;
  const forgetLine = (line, index) => acknowledged.current.delete(lineKey(line, index));
  return { confirmLines, editLine, forgetLine, isOpen: Boolean(request), dialog };
}
